import { useCallback } from 'react';

import type { Principal } from '../../../commonTypes.tsx';

import type { SpecificAccessLiveMutationArgs } from './specificAccessDialogTypes.ts';
import {
	buildUpdateContentDirectRestrictionsBulkRemoveVariables,
	buildUpdateContentDirectRestrictionsRemoveVariables,
	buildUpdateContentDirectRestrictionsVariables,
} from './specificAccessDialogDirectRestrictionsPayload.ts';
import type {
	useUpdateContentDirectRestrictionsMutation$data as MutationData,
	useUpdateContentDirectRestrictionsMutation$variables as MutationVariables,
} from '../../../../controller/mutations/__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';
import type { SpecificAccessFlags } from './useSpecificAccessFlags.tsx';

export type PrincipalById = Map<string, { principal: Principal }>;

type MutationPayload = MutationData['confluence_updateContentDirectRestrictions'];

/**
 * Checks if the mutation completed successfully without errors.
 * Centralizes the success/error check to ensure consistent behavior across all mutation paths.
 */
function isMutationSuccess(payload: MutationPayload): boolean {
	return Boolean(payload?.success && (!payload.errors || payload.errors.length === 0));
}

/**
 * Executes live mutations for the Specific Access dialog and surfaces the
 * server response so callers can reconcile local state with the authoritative
 * restriction changes.
 */
export function useSpecificAccessLiveMutation({
	cloudId,
	contentId,
	includeInvites,
	principalById,
	mutateDirectRestrictions,
	flags,
	onMutationApplied,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principalById: Map<string, { principal: Principal }>;
	mutateDirectRestrictions: (options: {
		variables: MutationVariables;
	}) => Promise<{ data?: MutationData } | undefined>;
	flags: SpecificAccessFlags;
	onMutationApplied?: (args: {
		payload: NonNullable<MutationData['confluence_updateContentDirectRestrictions']>;
		mutationArgs: SpecificAccessLiveMutationArgs;
	}) => void;
}): {
	triggerLiveMutation: (args: SpecificAccessLiveMutationArgs) => Promise<void>;
} {
	const triggerLiveMutation = useCallback(
		async (args: SpecificAccessLiveMutationArgs) => {
			try {
				if (args.type === 'remove-all') {
					const principalsToRemove: Principal[] = [];
					for (const id of args.principalIdsToRemove) {
						const principalEntry = principalById.get(id);
						if (principalEntry) {
							principalsToRemove.push(principalEntry.principal);
						}
					}

					if (principalsToRemove.length === 0) {
						return;
					}

					const bulkRemoveVariables = buildUpdateContentDirectRestrictionsBulkRemoveVariables({
						cloudId,
						contentId,
						includeInvites,
						principals: principalsToRemove,
					});

					const bulkRemoveRes = await mutateDirectRestrictions({ variables: bulkRemoveVariables });
					const bulkRemovePayload = bulkRemoveRes?.data?.confluence_updateContentDirectRestrictions;

					if (!isMutationSuccess(bulkRemovePayload)) {
						// TODO: add analytics
						flags.showAccessChangeFailedFlag();
						return;
					}

					if (bulkRemovePayload && onMutationApplied) {
						onMutationApplied({ payload: bulkRemovePayload, mutationArgs: args });
					}

					flags.showAccessUpdatedFlag();
					return;
				}

				const entry = principalById.get(args.principalId);
				if (!entry) {
					return;
				}

				const variables =
					args.type === 'remove'
						? buildUpdateContentDirectRestrictionsRemoveVariables({
								cloudId,
								contentId,
								includeInvites,
								principal: entry.principal,
							})
						: buildUpdateContentDirectRestrictionsVariables({
								cloudId,
								contentId,
								includeInvites,
								principal: entry.principal,
								previousRole: args.previousRole,
								nextRole: args.nextRole,
							});

				const res = await mutateDirectRestrictions({ variables });
				const payload = res?.data?.confluence_updateContentDirectRestrictions;
				if (!isMutationSuccess(payload)) {
					// TODO: add analytics
					flags.showAccessChangeFailedFlag();
					return;
				}

				if (payload && onMutationApplied) {
					onMutationApplied({ payload, mutationArgs: args });
				}

				flags.showAccessUpdatedFlag();
			} catch (e) {
				void e;
				flags.showAccessChangeFailedFlag();
			}
		},
		[
			cloudId,
			contentId,
			flags,
			includeInvites,
			mutateDirectRestrictions,
			onMutationApplied,
			principalById,
		],
	);

	return { triggerLiveMutation };
}
